import { OrganizationType, OrganizationTypeValue } from 'src/types/organization.type';

/**
 * The kind of organization behind a landing page / season planner. A youth club,
 * a university athletics department, a greek chapter and a community nonprofit
 * are different audiences: they get different planner shapes and different
 * landing modules. This file is the single place that knows the list, so the
 * planner/landing branches all agree on it.
 *
 * NOT a proceeds or payout concept — see `marginSplit`/`presentationMode` for
 * that. Mirrors the API enum in npt-api `src/utils/enum.ts`.
 */

export const DEFAULT_ORGANIZATION_TYPE: OrganizationTypeValue = 'YOUTH_SPORTS';

/** Display order in the admin picker. */
export const ORGANIZATION_TYPE_VALUES: OrganizationTypeValue[] = ['YOUTH_SPORTS', 'UNIVERSITY', 'GREEK_LIFE', 'COMMUNITY_NONPROFIT', 'SINGLE_EVENT'];

/** i18n keys under `organization.addedit.organizationType`, per value. */
export const ORGANIZATION_TYPE_I18N: Record<OrganizationTypeValue, { label: string; description: string }> = {
   YOUTH_SPORTS: {
      label: 'organization.addedit.organizationType.youthSports',
      description: 'organization.addedit.organizationType.youthSportsDescription',
   },
   UNIVERSITY: {
      label: 'organization.addedit.organizationType.university',
      description: 'organization.addedit.organizationType.universityDescription',
   },
   GREEK_LIFE: {
      label: 'organization.addedit.organizationType.greekLife',
      description: 'organization.addedit.organizationType.greekLifeDescription',
   },
   COMMUNITY_NONPROFIT: {
      label: 'organization.addedit.organizationType.communityNonprofit',
      description: 'organization.addedit.organizationType.communityNonprofitDescription',
   },
   SINGLE_EVENT: {
      label: 'organization.addedit.organizationType.singleEvent',
      description: 'organization.addedit.organizationType.singleEventDescription',
   },
};

export const isOrganizationTypeValue = (value: unknown): value is OrganizationTypeValue =>
   typeof value === 'string' && (ORGANIZATION_TYPE_VALUES as string[]).includes(value);

/**
 * Read an org's type with the platform default applied. Always use this rather
 * than `organization.organizationType` directly: the field is absent on cached
 * payloads written before organization types existed, and every such org is a
 * youth team by default.
 */
export const getOrganizationTypeValue = (organization?: Partial<OrganizationType> | null): OrganizationTypeValue =>
   isOrganizationTypeValue(organization?.organizationType) ? organization.organizationType : DEFAULT_ORGANIZATION_TYPE;
